import { format } from "date-fns";
import { useAtom } from "jotai";
import { Layers } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import EmptyState from "../../../shared/components/EmptyState";
import LoadingOverlay from "../../../shared/components/LoadingOverlay";
import PageHeader from "../../../shared/components/PageHeader";
import {
  overlayCategoryFilterAtom,
  overlayFiltersAtom,
  overlayLicenseFilterAtom,
  overlayPageAtom,
  overlaySearchAtom,
  overlayStatusFilterAtom,
} from "../atoms/overlaysAtoms";
import { useOverlaysList } from "../hooks/useOverlays";
import type {
  OverlayCategory,
  OverlayLicense,
  OverlayModerationStatus,
} from "../types";
import OverlayStatusBadge from "./OverlayStatusBadge";
import OverlayThumbnail from "./OverlayThumbnail";

const formatPrice = (priceArs?: number | null) => {
  if (priceArs == null || priceArs === 0) return "—";
  return new Intl.NumberFormat("es-AR", {
    style: "currency",
    currency: "ARS",
    maximumFractionDigits: 0,
  }).format(priceArs);
};

const OverlaysList = () => {
  const { t } = useTranslation("common");
  const [statusFilter, setStatusFilter] = useAtom(overlayStatusFilterAtom);
  const [categoryFilter, setCategoryFilter] = useAtom(
    overlayCategoryFilterAtom,
  );
  const [licenseFilter, setLicenseFilter] = useAtom(overlayLicenseFilterAtom);
  const [search, setSearch] = useAtom(overlaySearchAtom);
  const [page, setPage] = useAtom(overlayPageAtom);
  const [filters] = useAtom(overlayFiltersAtom);

  const { data, isLoading, isError, error } = useOverlaysList(filters);

  const statusTabs: { value: OverlayModerationStatus; label: string }[] = [
    { value: "pending", label: t("OVERLAYS.statusPending", "Pending") },
    { value: "approved", label: t("OVERLAYS.statusApproved", "Approved") },
    { value: "rejected", label: t("OVERLAYS.statusRejected", "Rejected") },
  ];

  const categoryOptions: { value: OverlayCategory | "all"; label: string }[] = [
    { value: "all", label: t("OVERLAYS.allCategories", "All categories") },
    { value: "custom", label: t("OVERLAYS.categoryCustom", "Custom") },
  ];

  const licenseOptions: { value: OverlayLicense | "all"; label: string }[] = [
    { value: "all", label: t("OVERLAYS.allLicenses", "All licenses") },
    { value: "free", label: t("OVERLAYS.licenseFree", "Free") },
    { value: "paid", label: t("OVERLAYS.licensePaid", "Paid") },
    { value: "community", label: t("OVERLAYS.licenseCommunity", "Community") },
  ];

  const handleStatusChange = (status: OverlayModerationStatus) => {
    setStatusFilter(status);
    setPage(1);
  };

  const overlays = data?.items ?? [];
  const total = data?.total ?? 0;
  const limit = filters.limit ?? 20;
  const totalPages = total > 0 ? Math.max(1, Math.ceil(total / limit)) : 1;

  return (
    <div className="flex flex-col gap-6">
      <PageHeader
        title={t("OVERLAYS.title", "Overlays")}
        subtitle={t(
          "OVERLAYS.subtitle",
          "Moderation queue for overlays submitted to the Store.",
        )}
      />

      <Card className="border-neutral-800 bg-neutral-900/50">
        <CardContent className="space-y-4 p-4">
          <div className="flex flex-wrap items-center gap-3">
            <div className="flex flex-wrap gap-2">
              {statusTabs.map((tab) => (
                <Button
                  key={tab.value}
                  variant={statusFilter === tab.value ? "default" : "outline"}
                  size="sm"
                  onClick={() => handleStatusChange(tab.value)}
                  className={
                    statusFilter === tab.value
                      ? "bg-purple-600 text-white hover:bg-purple-700"
                      : "border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  }
                >
                  {tab.label}
                </Button>
              ))}
            </div>

            <select
              value={categoryFilter}
              onChange={(e) => {
                setCategoryFilter(e.target.value as OverlayCategory | "all");
                setPage(1);
              }}
              className="h-9 rounded-md border border-neutral-700 bg-neutral-800/50 px-3 text-sm text-neutral-200"
            >
              {categoryOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <select
              value={licenseFilter}
              onChange={(e) => {
                setLicenseFilter(e.target.value as OverlayLicense | "all");
                setPage(1);
              }}
              className="h-9 rounded-md border border-neutral-700 bg-neutral-800/50 px-3 text-sm text-neutral-200"
            >
              {licenseOptions.map((opt) => (
                <option key={opt.value} value={opt.value}>
                  {opt.label}
                </option>
              ))}
            </select>

            <div className="flex-1 min-w-[200px]">
              <Input
                type="text"
                placeholder={t(
                  "OVERLAYS.searchPlaceholder",
                  "Search by name or author...",
                )}
                value={search}
                onChange={(e) => {
                  setSearch(e.target.value);
                  setPage(1);
                }}
                className="border-neutral-700 bg-neutral-800/50 text-neutral-200"
              />
            </div>
          </div>

          {isLoading ? (
            <div className="py-12">
              <LoadingOverlay />
            </div>
          ) : isError ? (
            <EmptyState
              title={t("OVERLAYS.errorTitle", "Could not load overlays")}
              description={
                error instanceof Error
                  ? error.message
                  : t("OVERLAYS.errorDescription", "Try again in a moment.")
              }
            />
          ) : overlays.length === 0 ? (
            <EmptyState
              icon={<Layers className="h-8 w-8" />}
              title={t("OVERLAYS.emptyTitle", "No overlays in this view")}
              description={t(
                "OVERLAYS.emptyDescription",
                "There are no overlays matching the current filters.",
              )}
            />
          ) : (
            <>
              <Table>
                <TableHeader>
                  <TableRow className="border-neutral-800 hover:bg-transparent">
                    <TableHead className="text-neutral-400">
                      {t("OVERLAYS.colPreview", "Preview")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("OVERLAYS.colName", "Name")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("OVERLAYS.colAuthor", "Author")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("OVERLAYS.colCategory", "Category")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("OVERLAYS.colLicense", "License")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("OVERLAYS.colPrice", "Price")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("OVERLAYS.colStatus", "Status")}
                    </TableHead>
                    <TableHead className="text-neutral-400">
                      {t("OVERLAYS.colSubmitted", "Submitted")}
                    </TableHead>
                    <TableHead className="text-right text-neutral-400">
                      {t("OVERLAYS.colActions", "Actions")}
                    </TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {overlays.map((overlay) => (
                    <TableRow
                      key={overlay.id}
                      className="border-neutral-800 hover:bg-neutral-800/50"
                    >
                      <TableCell>
                        <OverlayThumbnail
                          url={overlay.previewThumbnailUrl}
                          alt={overlay.name}
                        />
                      </TableCell>
                      <TableCell className="text-neutral-200">
                        <div className="flex flex-col">
                          <span className="font-medium">{overlay.name}</span>
                          <span className="text-xs text-neutral-500">
                            v{overlay.version}
                          </span>
                        </div>
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {overlay.authorName}
                      </TableCell>
                      <TableCell>
                        <Badge
                          variant="outline"
                          className="border-neutral-600 text-neutral-300"
                        >
                          {overlay.category}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {overlay.license}
                      </TableCell>
                      <TableCell className="text-neutral-300">
                        {formatPrice(overlay.priceArs)}
                      </TableCell>
                      <TableCell>
                        <OverlayStatusBadge status={overlay.status} />
                      </TableCell>
                      <TableCell className="text-neutral-400 text-sm">
                        {format(new Date(overlay.createdAt), "dd MMM yyyy")}
                      </TableCell>
                      <TableCell className="text-right">
                        <Button
                          asChild
                          variant="ghost"
                          size="sm"
                          className="text-purple-400 hover:bg-purple-500/10 hover:text-purple-300"
                        >
                          <Link to={`/overlays/${overlay.slug}`}>
                            {t("OVERLAYS.review", "Review")}
                          </Link>
                        </Button>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>

              {totalPages > 1 && (
                <div className="flex items-center justify-between border-t border-neutral-800 pt-4">
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.max(1, p - 1))}
                    disabled={page === 1}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    {t("OVERLAYS.prev", "Previous")}
                  </Button>
                  <span className="text-sm text-neutral-400">
                    {t("OVERLAYS.pageOf", "Page")} {page} / {totalPages}
                  </span>
                  <Button
                    variant="outline"
                    size="sm"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page >= totalPages}
                    className="border-neutral-700 text-neutral-300 hover:bg-neutral-700"
                  >
                    {t("OVERLAYS.next", "Next")}
                  </Button>
                </div>
              )}
            </>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

export default OverlaysList;
